import OlMap from 'ol/Map';
import Feature, { FeatureLike } from 'ol/Feature';
import Point from 'ol/geom/Point';
import VectorLayer from 'ol/layer/Vector';
import VectorSource from 'ol/source/Vector';
import { Coordinate } from 'ol/coordinate';
import { fromLonLat, transform } from 'ol/proj';
import { Circle as CircleStyle, Fill, Icon, Stroke, Style } from 'ol/style';
import { firstValueFrom } from 'rxjs';
import { BlastHolesDTO, HoleDTO, PositionXYZ } from 'src/app/services/hole.service';
import { HttpService } from 'src/app/services/http.service';
import { FEATURE_BLAST_ID, FEATURE_BLAST_NAME, FEATURE_HOLE, FEATURE_KIND, MAP_PROJECTION, Z_INDEX } from '../site-map.constants';
import { HoleFeatureFilter, HoleLegends, HoleStatusFilter } from '../site-map.types';
import { EMPTY_HOLE_LEGENDS, computeHoleLegends, holeIcon } from '../utils/hole-appearance';

const HOLE_HOVER_STYLE = new Style({
  image: new CircleStyle({
    radius: 6,
    fill: new Fill({ color: 'rgba(255, 255, 255, 0.5)' }),
    stroke: new Stroke({ color: 'rgba(255, 0, 0, 0.9)' }),
  }),
});

/** Première position portant des x et y finis, null si aucune. */
function firstPositionWithXY(...positions: (PositionXYZ | null | undefined)[]): PositionXYZ | null {
  return positions.find(position => !!position && Number.isFinite(position.x) && Number.isFinite(position.y)) ?? null;
}

export class HolesLayer {
  private readonly source = new VectorSource<Feature<Point>>();
  private readonly layer = new VectorLayer({
    source: this.source,
    zIndex: Z_INDEX.holes,
    visible: false,
    style: feature => this.styleFor(feature),
  });
  private readonly blastsById = new Map<number, BlastHolesDTO>();
  private readonly featuresByBlast = new Map<number, Feature<Point>[]>();
  private readonly requestedBlastIds = new Set<number>();
  private readonly iconStyles = new Map<string, Style>();

  private siteId: number | null = null;
  /** Projection du site, enregistrée auprès d'OpenLayers, dans laquelle sont exprimées les positions XYZ des trous. */
  private siteProjection: string | null = null;
  private generation = 0;
  private displayedBlastIds: number[] = [];
  private renderedKey = '';
  private statusFilter: HoleStatusFilter = 'actual';
  private featureFilter: HoleFeatureFilter = 'all';
  private hovered: FeatureLike | null = null;

  constructor(
    private readonly map: OlMap,
    private readonly http: HttpService,
    private readonly onLegendsChange: (legends: HoleLegends) => void,
  ) {
    map.addLayer(this.layer);
  }

  /** Affiche les trous des tirs donnés (cochés et visibles à l'écran) et charge ceux qui ne sont pas encore en cache. */
  async show(siteId: number, blastIds: number[]): Promise<void> {
    if (siteId !== this.siteId) this.resetSite(siteId);
    this.layer.setVisible(true);
    this.displayedBlastIds = blastIds;
    this.render();

    const missing = blastIds.filter(id => !this.requestedBlastIds.has(id));
    if (!missing.length) return;

    const generation = this.generation;
    missing.forEach(id => this.requestedBlastIds.add(id));
    try {
      const blasts = await firstValueFrom(this.http.post<number[], BlastHolesDTO[]>('blasts/blastHoles', missing));
      if (generation !== this.generation) return;
      (blasts ?? []).forEach(blast => {
        this.blastsById.set(blast.blastId, blast);
        this.featuresByBlast.set(blast.blastId, this.toFeatures(blast));
      });
      this.renderedKey = '';
      if (this.layer.getVisible()) this.render();
    } catch {
      if (generation === this.generation) missing.forEach(id => this.requestedBlastIds.delete(id));
    }
  }

  /**
   * Projection (enregistrée) du site courant, null si inconnue : les trous déjà chargés sont repositionnés,
   * leurs positions XYZ étant préférées aux positions WGS84 (LGZ) dès qu'elle est disponible.
   */
  setSiteProjection(projection: string | null): void {
    if (projection === this.siteProjection) return;
    this.siteProjection = projection;
    this.hovered = null;
    this.blastsById.forEach((blast, blastId) => this.featuresByBlast.set(blastId, this.toFeatures(blast)));
    this.renderedKey = '';
    if (this.layer.getVisible()) this.render();
  }

  hide(): void {
    this.layer.setVisible(false);
    this.displayedBlastIds = [];
    this.renderedKey = '';
    this.source.clear();
    this.onLegendsChange(EMPTY_HOLE_LEGENDS);
  }

  setFilters(statusFilter: HoleStatusFilter, featureFilter: HoleFeatureFilter): void {
    if (statusFilter === this.statusFilter && featureFilter === this.featureFilter) return;
    this.statusFilter = statusFilter;
    this.featureFilter = featureFilter;
    this.layer.changed();
    this.emitLegends();
  }

  setHovered(feature: FeatureLike | null): void {
    if (feature === this.hovered) return;
    this.hovered = feature;
    this.layer.changed();
  }

  dispose(): void {
    this.generation++;
    this.map.removeLayer(this.layer);
    this.source.clear();
    this.blastsById.clear();
    this.featuresByBlast.clear();
    this.iconStyles.clear();
  }

  private resetSite(siteId: number): void {
    this.generation++;
    this.siteId = siteId;
    this.blastsById.clear();
    this.featuresByBlast.clear();
    this.requestedBlastIds.clear();
    this.renderedKey = '';
    this.hovered = null;
    this.source.clear();
  }

  private render(): void {
    const key = [...this.displayedBlastIds].sort((a, b) => a - b).join(',');
    if (key === this.renderedKey) return;
    this.renderedKey = key;

    this.source.clear(true);
    this.source.addFeatures(this.displayedBlastIds.flatMap(id => this.featuresByBlast.get(id) ?? []));
    this.emitLegends();
  }

  private emitLegends(): void {
    const holes = this.source.getFeatures().map(feature => feature.get(FEATURE_HOLE) as HoleDTO);
    this.onLegendsChange(computeHoleLegends(holes, this.statusFilter, this.featureFilter));
  }

  private toFeatures(blast: BlastHolesDTO): Feature<Point>[] {
    return (blast.holes ?? []).flatMap(hole => {
      const coordinate = this.holeCoordinate(hole);
      if (!coordinate) return [];
      const feature = new Feature({ geometry: new Point(coordinate) });
      feature.setProperties({
        [FEATURE_KIND]: 'hole',
        [FEATURE_BLAST_ID]: blast.blastId,
        [FEATURE_BLAST_NAME]: blast.blastName,
        [FEATURE_HOLE]: hole,
      }, true);
      return [feature];
    });
  }

  /**
   * Position carte d'un trou, comme la couche historique : position XYZ (prévue puis mesurée) dans la projection
   * du site quand elle est connue, sinon position WGS84 (LGZ) ; null si aucune n'est exploitable.
   */
  private holeCoordinate(hole: HoleDTO): Coordinate | null {
    const xyz = firstPositionWithXY(hole.PositionPlannedXYZ, hole.PositionMeasuredXYZ);
    if (xyz && this.siteProjection) {
      try {
        return transform([xyz.x, xyz.y], this.siteProjection, MAP_PROJECTION);
      } catch {
        // Projection inutilisable : repli sur la position WGS84.
      }
    }
    const lgz = hole.positionLGZ;
    return lgz && Number.isFinite(lgz.longitude) && Number.isFinite(lgz.latitude)
      ? fromLonLat([lgz.longitude, lgz.latitude])
      : null;
  }

  private styleFor(feature: FeatureLike): Style {
    if (feature === this.hovered) return HOLE_HOVER_STYLE;

    const src = holeIcon(feature.get(FEATURE_HOLE), this.statusFilter, this.featureFilter);
    let style = this.iconStyles.get(src);
    if (!style) {
      style = new Style({ image: new Icon({ src, scale: 0.8, anchor: [0.5, 0.5] }), zIndex: 100 });
      this.iconStyles.set(src, style);
    }
    return style;
  }
}
